import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import {
  LICENSE_SERVICE_ACTION_ACTIVE,
  LICENSE_SERVICE_STATUS_ACTIVE,
  LICENSE_SERVICE_STATUS_INACTIVE,
  MSP_DAILY_CREDITS,
  ORG_SERVICE_STATUS_PRO_KEY,
  UNITY_PRO_ACCESS_POINT_DAILY_CREDITS,
  UNITY_PRO_SERVICE_KEY,
  UNITY_PRO_SWITCH_DAILY_CREDITS,
} from "const/license";

// Randomize data for dynamic UI testing
const DAILY_USAGE = parseFloat((Math.random() * (50.0 - 5.0) + 5.0).toFixed(1)); // 5.0 ~ 50.0
const randomRemainingDays = Math.floor(Math.random() * (365 - 30) + 30); // 30 ~ 365 days
const calculatedBalance = Math.floor(randomRemainingDays * DAILY_USAGE);

const initialState = {
  mspServiceStatus: {
    default: {
      data: {
        name: "New MSP",
        service: {
          unityMsp: true,
        },
      },
    },
    "ent-ntt-managed-service": {
      data: {
        name: "NTT Managed Service",
        service: {
          unityMsp: true,
        },
      },
    },
    "ent-global-network-services": {
      data: {
        name: "Global Network Services",
        service: {
          unityMsp: false,
        },
      },
    },
  },
  mspDashboard: {
    default: {
      data: {
        balance: 3000,
        dailyUsage: 25,
        remainingDays: 120,
        nextDeduction: 750,
        nextDeductionAt: dayjs.utc().add(1, "month").startOf("month").unix(),
      },
    },
    "ent-ntt-managed-service": {
      data: {
        balance: 3000,
        dailyUsage: 25,
        remainingDays: 120,
        nextDeduction: 750,
        nextDeductionAt: dayjs.utc().add(1, "month").startOf("month").unix(),
      },
    },
    "ent-global-network-services": {
      data: {
        balance: 0,
        dailyUsage: 25,
        remainingDays: 0,
        nextDeduction: 750,
        nextDeductionAt: dayjs.utc().add(1, "month").startOf("month").unix(),
      },
    },
  },
  mspOverview: {
    data: {
      treasury: {
        balance: 308000,
        currency: "credits",
      },
      organizations: [
        {
          id: "org-dlink-headquarters",
          name: "D-Link Headquarters",
          subscriptionStatus: "Active",
          billableDevices: 85,
          balance: 18420,
          dailyUsage: 168,
          alert: "~110 days",
          daysLeft: -5040,
          subscription: {
            serviceName: "NMS-MSP",
            dailyCost: 168,
            daysLeft: 110,
            credits: 18420,
            gatewayCount: 1,
            activationDays: 30,
          },
        },
        {
          id: "org-global-operations",
          name: "Global Operations",
          subscriptionStatus: "Active",
          billableDevices: 42,
          balance: 9860,
          dailyUsage: 95,
          alert: "~104 days",
          daysLeft: -2850,
          subscription: {
            serviceName: "NMS-MSP",
            dailyCost: 95,
            daysLeft: 104,
            credits: 9860,
            gatewayCount: 1,
            activationDays: 30,
          },
        },
        {
          id: "org-enterprise-services",
          name: "Enterprise Services",
          subscriptionStatus: "Active",
          billableDevices: 22,
          balance: 0,
          dailyUsage: 215,
          alert: "~0 days",
          daysLeft: 0,
          subscription: {
            serviceName: "NMS-MSP",
            dailyCost: 215,
            daysLeft: 82,
            credits: 6200,
            gatewayCount: 1,
            activationDays: 30,
          },
        },
        {
          id: "org-north-america",
          name: "North America Region",
          subscriptionStatus: "Active",
          billableDevices: 36,
          balance: 2640,
          dailyUsage: 82,
          alert: "~32 days",
          daysLeft: -2460,
          subscription: {
            serviceName: "NMS-MSP",
            dailyCost: 82,
            daysLeft: 32,
            credits: 2640,
            gatewayCount: 1,
            activationDays: 30,
          },
        },
        {
          id: "org-asia-pacific",
          name: "Asia Pacific Region",
          subscriptionStatus: "Inactive",
          billableDevices: 12,
          balance: 0,
          dailyUsage: 0,
          alert: "No Subscription",
          daysLeft: 0,
          subscription: {
            serviceName: "NMS-MSP",
            dailyCost: 180,
            daysLeft: null,
            credits: 0,
            gatewayCount: 1,
            activationDays: 30,
          },
        },
      ],
    },
  },
  mspOrgLicenseSummary: {
    data: [
      {
        orgId: "org-dlink-headquarters",
        orgName: "D-Link Headquarters",
        balance: 18420,
        dailyUsage: 168,
        remainingDays: 110,
        alertStatus: "NORMAL",
        sites: [{ id: "dlink-hq", name: "Headquarters", accessPoints: 35, switches: 50, services: { unityPro: true } }],
      },
      {
        orgId: "org-global-operations",
        orgName: "Global Operations",
        balance: 9860,
        dailyUsage: 95,
        remainingDays: 104,
        alertStatus: "NORMAL",
        sites: [{ id: "global-ops", name: "Operations", accessPoints: 22, switches: 20, services: { unityPro: true } }],
      },
      {
        orgId: "org-enterprise-services",
        orgName: "Enterprise Services",
        balance: 0,
        dailyUsage: 215,
        remainingDays: 0,
        alertStatus: "DEDUCT_FAILED",
        sites: [{ id: "enterprise-services", name: "Services", accessPoints: 10, switches: 12, services: { unityPro: true } }],
      },
      {
        orgId: "org-north-america",
        orgName: "North America Region",
        balance: 2640,
        dailyUsage: 82,
        remainingDays: 32,
        alertStatus: "NORMAL",
        sites: [{ id: "north-america", name: "North America", accessPoints: 18, switches: 18, services: { unityPro: true } }],
      },
      {
        orgId: "org-asia-pacific",
        orgName: "Asia Pacific Region",
        balance: 0,
        dailyUsage: 0,
        remainingDays: 0,
        alertStatus: "NORMAL",
        sites: [{ id: "asia-pacific", name: "Asia Pacific", accessPoints: 8, switches: 4, services: { unityPro: false } }],
      },
    ],
  },
  mspImport: {
    data: [],
  },
  mspTransfer: {
    data: null,
  },
  mspSubscriptionToggle: {
    data: null,
  },
  licensePreConfirm: {
    data: [
      {
        siteId: "taipei-uuid",
        siteName: "Taipei",
        service: [UNITY_PRO_SERVICE_KEY],
        action: "add",
        deviceCount: 1,
        deviceTotalCount: 9,
        rate: "1 credit/day/device",
        dailyRate: 1,
        remainingDays: 30,
        sufficientCredits: true,
      }
    ]
  },
  dashboard: {
    data: {
      balance: calculatedBalance,
      dailyUsage: DAILY_USAGE,
      remainingDays: randomRemainingDays,
      nextDeduction: Math.floor(DAILY_USAGE * 30),
      nextDeductionAt: dayjs.utc().add(1, 'month').startOf('month').unix()
    }
  },
  serviceStatus: {
    data: {
      msp: {
        enabled: false,
        name: "",
        walletBalance: 0
      },
      org: {
        name: "Org Test",
        underMsp: false,
        walletBalance: calculatedBalance,
        aiChatbotEnabled: true,
        nmsMspEnabled: false,
        sites: [
          {
            id: "taipei-uuid",
            name: "Taipei",
            accessPoints: 5,
            switches: 3,
            services: {
              [ORG_SERVICE_STATUS_PRO_KEY]: false,
            }
          },
          {
            id: "kaohsiung-uuid",
            name: "Kaohsiung",
            accessPoints: 12,
            switches: 4,
            services: {
              [ORG_SERVICE_STATUS_PRO_KEY]: true,
            }
          }
        ]
      }
    }
  },
  siteServiceStatus: {
    data: {
      name: "New Site1234",
      accessPoints: 5,
      switches: 2,
      services: {
        [UNITY_PRO_SERVICE_KEY]: true,
      }
    }
  },
  subscriptions: {
    data: {
      sites: [
        { id: "taipei-uuid", name: "Taipei" },
        { id: "kaohsiung-uuid", name: "Kaohsiung" }
      ],
      orgLevel: [
        { id: 'ai-chatbot', name: 'AI Chatbot', status: 'Active', price: '1 Credit/day', scope: 'Organization-wide' },
        { id: 'nms-msp', name: 'NMS MSP', status: 'Inactive', price: '10 Credits/day', scope: 'Organization-wide' }
      ],
      siteLevel: [
        { id: 'health-ai', name: 'Health AI Insight', price: '1 Credit/device/day' },
        { id: 'nms-pro', name: 'NMS Pro', price: '1 Credit/device/day' }
      ],
      siteSubscriptions: {
        'taipei-uuid': { 'health-ai': 'Active', 'nms-pro': 'Inactive' },
        'kaohsiung-uuid': { 'health-ai': 'Inactive', 'nms-pro': 'Active' }
      }
    }
  },
  baseRates: {
    data: {
      msp: {
        base: 15,
        [ORG_SERVICE_STATUS_PRO_KEY]: 1,
        healthAI: 1
      },
      nonMsp: {
        base: 0,
        [ORG_SERVICE_STATUS_PRO_KEY]: 1,
        healthAI: 1
      },
      aiChatbot: 1
    }
  },
  deviceCounts: {
    data: {
      "taipei-uuid": 8,
      "kaohsiung-uuid": 16
    }
  },
  orgHistory: {
    data: [
      { timestamp: dayjs("2026-09-05 10:30:00").valueOf(), type: "Import", descriptionParams: { Account: "admin@techcorp.com", Key: "UNTP-•••••-•••••-•••••-8K2M", TxnId: "ORG-IMP-0905" }, change: 5000, balance: 12450 },
      { timestamp: dayjs("2026-09-01 00:05:00").valueOf(), type: "MonthlyCharge", descriptionParams: { Site: "Taipei", Devices: 8, Service: "Unity Pro", Rate: "AP 1 · Switch 1 credit/day", Days: 30, Period: "2026-09-01 to 2026-09-30", TxnId: "ORG-MON-0901" }, change: -240, balance: 7450 },
      { timestamp: dayjs("2026-08-26 14:20:00").valueOf(), type: "AddedDevice", descriptionParams: { Site: "Taipei", Devices: 2, Service: "Unity Pro", Rate: "AP 1 · Switch 1 credit/day", Days: 6, Period: "2026-08-26 to 2026-08-31", TxnId: "ORG-ADD-0826" }, change: -12, balance: 7690 },
      { timestamp: dayjs("2026-08-20 09:45:00").valueOf(), type: "RemoveDevice", descriptionParams: { Site: "Kaohsiung", Device: 1, Service: "Unity Pro", Rate: "AP 1 · Switch 1 credit/day", Days: 11, Period: "2026-08-20 to 2026-08-31", TxnId: "ORG-REM-0820" }, change: 11, balance: 7702 },
      { timestamp: dayjs("2026-08-15 11:00:00").valueOf(), type: "Subscribe", descriptionParams: { Site: "Taichung", Device: 6, Service: "Unity Pro", Rate: "AP 1 · Switch 1 credit/day", Days: 17, Period: "2026-08-15 to 2026-08-31", TxnId: "ORG-SUB-0815" }, change: -102, balance: 7691 },
      { timestamp: dayjs("2026-08-10 16:25:00").valueOf(), type: "Purchase", descriptionParams: { Account: "admin@techcorp.com", OrderReference: "PO-20260810-01", GrantedCredits: 3000, TxnId: "ORG-PUR-0810" }, change: 3000, balance: 7793 },
      { timestamp: dayjs("2026-08-08 13:10:00").valueOf(), type: "Reversal", descriptionParams: { OrderReference: "PO-20260810-01", OriginalGrant: 3000, ThisReversal: 500, CumulativeReversed: 500, ReclaimedInFull: "No", TxnId: "ORG-REV-0808" }, change: -500, balance: 4793 },
      { timestamp: dayjs("2026-08-05 08:30:00").valueOf(), type: "CoverCurrentMonth", descriptionParams: { Site: "Taipei", Device: 8, Service: "Unity Pro", Rate: "AP 1 · Switch 1 credit/day", Days: 27, Period: "2026-08-05 to 2026-08-31", Settles: "Current-month service", Actor: "admin@techcorp.com", TxnId: "ORG-COV-0805" }, change: -216, balance: 5293 },
      { timestamp: dayjs("2026-08-03 15:40:00").valueOf(), type: "PartialGrant", descriptionParams: { Site: "Kaohsiung", Device: 4, Service: "Unity Pro", Rate: "AP 1 · Switch 1 credit/day", DaysGranted: 14, DaysUnbilled: 14, Period: "2026-08-03 to 2026-08-31", Actor: "support@dlink.com", TxnId: "ORG-PGR-0803" }, change: 56, balance: 5509 },
      { timestamp: dayjs("2026-08-01 10:15:00").valueOf(), type: "MSPDisbursement", descriptionParams: { MSP: "D-Link MSP", Account: "msp-admin@dlink.com", TxnId: "ORG-DIS-0801" }, change: 2000, balance: 5453 },
      { timestamp: dayjs("2026-07-28 17:05:00").valueOf(), type: "MSPWithdrawal", descriptionParams: { MSP: "D-Link MSP", Account: "msp-admin@dlink.com", TxnId: "ORG-WIT-0728" }, change: -300, balance: 3453 },
      { timestamp: dayjs("2026-07-25 12:00:00").valueOf(), type: "ManualAdjustment", descriptionParams: { Operator: "support@dlink.com", ApprovedBy: "finance@dlink.com", Reason: "Service credit correction", TxnId: "ORG-ADJ-0725" }, change: 120, balance: 3753 },
      { timestamp: dayjs("2026-07-20 09:30:00").valueOf(), type: "Unsubscribe", descriptionParams: { Site: "Tainan", Device: 3, Service: "Unity Pro", Rate: "AP 1 · Switch 1 credit/day", Days: 12, Period: "2026-07-20 to 2026-07-31", TxnId: "ORG-UNS-0720" }, change: 36, balance: 3633 },
    ],
  },
  mspHistory: {
    data: [
      { timestamp: dayjs("2026-09-05 09:20:00").valueOf(), type: "Import", descriptionParams: { Account: "msp-admin@dlink.com", Key: "MSP-•••••-•••••-•••••-7F9Q", TxnId: "MSP-IMP-0905" }, change: 10000, balance: 308000 },
      { timestamp: dayjs("2026-09-02 13:45:00").valueOf(), type: "Purchase", descriptionParams: { Account: "msp-admin@dlink.com", OrderReference: "MSP-PO-20260902", GrantedCredits: 50000, TxnId: "MSP-PUR-0902" }, change: 50000, balance: 298000 },
      { timestamp: dayjs("2026-08-30 16:10:00").valueOf(), type: "Reversal", descriptionParams: { OrderReference: "MSP-PO-20260902", OriginalGrant: 50000, ThisReversal: 5000, CumulativeReversed: 5000, ReclaimedInFull: "No", TxnId: "MSP-REV-0830" }, change: -5000, balance: 248000 },
      { timestamp: dayjs("2026-08-25 11:30:00").valueOf(), type: "MSPDisbursement", descriptionParams: { Organization: "TechCorp HQ", Account: "org-admin@techcorp.com", TxnId: "MSP-DIS-0825" }, change: -2000, balance: 253000 },
      { timestamp: dayjs("2026-08-18 14:00:00").valueOf(), type: "MSPWithdrawal", descriptionParams: { Organization: "TechCorp HQ", Account: "org-admin@techcorp.com", TxnId: "MSP-WIT-0818" }, change: 300, balance: 255000 },
      { timestamp: dayjs("2026-08-12 10:05:00").valueOf(), type: "CompensatoryKeyIssued", descriptionParams: { Key: "COMP-•••••-•••••-•••••-3A1D", KeyValue: 1000, SentTo: "support@techcorp.com", Reason: "Approved service interruption", TxnId: "MSP-CMP-0812" }, change: -1000, balance: 254700 },
      { timestamp: dayjs("2026-08-01 08:00:00").valueOf(), type: "ManualAdjustment", descriptionParams: { Operator: "finance@dlink.com", ApprovedBy: "msp-owner@dlink.com", Reason: "Treasury reconciliation", TxnId: "MSP-ADJ-0801" }, change: 250, balance: 255700 },
    ],
  },
  subscribeResult: {
    data: {
      status: "success"
    }
  },
  import: {
    data: Array.from({ length: 50 }, (_, i) => {
      const keys = ["H7K9", "W2NP", "R4X6", "M1VB", "L8QS", "G3F5", "T7Y1", "A9Z4", "E2D8", "C6K0", "V2J6", "T9F4", "C1G8", "Z5D3", "S7H1", "X4R9", "L2N6", "Q8K1", "W5V7", "B3M0"];
      const getRand = (arr) => arr[Math.floor(Math.random() * arr.length)];
      const licenseKey = `${getRand(keys)}-${getRand(keys)}-${getRand(keys)}-${getRand(keys)}-${getRand(keys)}`;
      const isError = i % 10 === 0; // Every 10th is an error
      return isError ? {
        licenseKey,
        status: "error",
        error: {
          code: 500,
          message: "The license key invalid"
        }
      } : {
        licenseKey,
        status: "success",
        change: 2000,
        balance: 17000 + (i * 2000)
      };
    })
  }
};

export const licenseSlice = createSlice({
  name: 'license',
  initialState,
  reducers: {
    addMspTreasuryCredits: (state, action) => {
      const licenseKeys = action.payload?.data?.licenseKeys || [];
      const creditsPerKey = 2000;
      const addedCredits = licenseKeys.length * creditsPerKey;
      const entId = action.payload?.entId;
      const mspDashboard = state.mspDashboard[entId] || state.mspDashboard.default;

      state.mspOverview.data.treasury.balance += addedCredits;
      mspDashboard.data.balance += addedCredits;
      state.mspImport.data = licenseKeys.map((licenseKey) => ({
        licenseKey,
        status: "success",
        change: creditsPerKey,
        balance: mspDashboard.data.balance,
      }));
    },
    transferMspCredits: (state, action) => {
      const { orgId: organizationId, action: transferAction, amount } = action.payload || {};
      const transferAmount = Number(amount);
      const organization = state.mspOrgLicenseSummary.data.find(
        (item) => item.orgId === organizationId,
      );

      if (!organization || !Number.isFinite(transferAmount) || transferAmount <= 0) {
        state.mspTransfer.data = { status: "error", message: "Invalid transfer request." };
        return;
      }

      if (transferAction === "disburse") {
        state.mspDashboard.default.data.balance -= transferAmount;
        organization.balance += transferAmount;
      } else if (transferAction === "withdraw") {
        state.mspDashboard.default.data.balance += transferAmount;
        organization.balance -= transferAmount;
      } else {
        state.mspTransfer.data = { status: "error", message: "Invalid transfer action." };
        return;
      }

      state.mspTransfer.data = {
        status: "success",
        organizationId,
        action: transferAction,
        amount: transferAmount,
        type: transferAction === "disburse" ? "Disburse" : "Withdraw",
        metadata: {
          msp: "Fr_test",
          account: "rocks@unity.nuclias.com",
        },
      };
    },
    toggleMspSubscription: (state, action) => {
      const { organizationId, activate } = action.payload?.data || {};
      const organization = state.mspOverview.data.organizations.find(
        (item) => item.id === organizationId,
      );

      if (!organization) {
        state.mspSubscriptionToggle.data = {
          status: "error",
          message: "Organization not found.",
        };
        return;
      }

      const subscription = organization.subscription || {};
      const dailyCost = subscription.dailyCost || organization.dailyUsage || 0;
      const activationDays = subscription.activationDays ?? 0;

      organization.subscriptionStatus = activate
        ? LICENSE_SERVICE_STATUS_ACTIVE
        : LICENSE_SERVICE_STATUS_INACTIVE;
      organization.dailyUsage = activate ? dailyCost : 0;
      organization.daysLeft = activate ? -(subscription.credits || dailyCost * activationDays) : 0;
      organization.alert = activate
        ? `~${activationDays} days`
        : "No Subscription";
      organization.subscription = {
        ...subscription,
        daysLeft: activate ? activationDays : null,
        credits: activate ? subscription.credits || dailyCost * activationDays : 0,
      };
      state.mspSubscriptionToggle.data = {
        status: "success",
        organizationId,
        activate,
      };
    },
    setLicenseDashboard: (state, action) => {
      state.dashboard = action.payload;
    },
    setLicenseServiceStatus: (state, action) => {
      state.serviceStatus = action.payload;
    },
    setLicenseHistory: (state, action) => {
      state.history = action.payload;
    },
    setLicenseSubscriptions: (state, action) => {
      state.subscriptions = action.payload;
    },
    updateSubscriptionStatus: (state, action) => {
      const payload = action.payload;
      let siteId, serviceKey, status;

      // Extract details from payload
      if (payload.data) {
        siteId = payload.data.siteId;
        serviceKey = payload.data.service; // e.g., UNITY_PRO_SERVICE_KEY
        status = payload.data.action === LICENSE_SERVICE_ACTION_ACTIVE; // boolean
      } else {
        siteId = payload.siteId;
        serviceKey = payload.serviceId === "health-ai" ? "healthAI" : payload.serviceId === "nms-pro" ? ORG_SERVICE_STATUS_PRO_KEY : payload.serviceId;
        status =
          payload.status === LICENSE_SERVICE_STATUS_ACTIVE ||
          payload.status === true;
      }

      // 1. Update serviceStatus (The single source of truth for mock now)
      if (siteId) {
        const site = state.serviceStatus.data.org.sites.find(s => s.id === siteId);
        if (site && site.services) {
          // Org service status reports the Pro flag under a different key
          // from the Unity Pro subscribe request.
          site.services[
            serviceKey === UNITY_PRO_SERVICE_KEY
              ? ORG_SERVICE_STATUS_PRO_KEY
              : serviceKey
          ] = status;
        }

        // Keep state.subscriptions in sync if it exists (legacy/fallback support)
        if (state.subscriptions.data.siteSubscriptions[siteId]) {
          const subKey =
            serviceKey === UNITY_PRO_SERVICE_KEY ? "unity-pro" : serviceKey;
          state.subscriptions.data.siteSubscriptions[siteId][subKey] = status
            ? LICENSE_SERVICE_STATUS_ACTIVE
            : LICENSE_SERVICE_STATUS_INACTIVE;
        }
      }

      // 2. Recalculate Daily Usage (Daily Burn Rate) for Mock
      let totalDailyUsage = state.serviceStatus.data.org.underMsp
        ? MSP_DAILY_CREDITS
        : 0;
      state.serviceStatus.data.org.sites.forEach(site => {
        if (site.services[ORG_SERVICE_STATUS_PRO_KEY]) {
          totalDailyUsage +=
            (Number(site.accessPoints) || 0) *
            UNITY_PRO_ACCESS_POINT_DAILY_CREDITS +
            (Number(site.switches) || 0) *
            UNITY_PRO_SWITCH_DAILY_CREDITS;
        }
      });

      state.dashboard.data.dailyUsage = parseFloat(totalDailyUsage.toFixed(1));
    },
  },
});

export const {
  addMspTreasuryCredits,
  toggleMspSubscription,
  transferMspCredits,
  setLicenseDashboard,
  setLicenseServiceStatus,
  setLicenseHistory,
  setLicenseSubscriptions,
  updateSubscriptionStatus
} = licenseSlice.actions;

export default licenseSlice.reducer;
